import { API_BASE, BACKEND_URL } from "./config";
import { tokens } from "./tokens";

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function doFetch(
  path,
  { method = "GET", body, signal, auth = true } = {},
) {
  const headers = {};
  if (body) headers["Content-Type"] = "application/json";
  if (auth && tokens.access) headers.Authorization = `Bearer ${tokens.access}`;

  return fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
}

async function doRefresh() {
  const refreshToken = tokens.refresh;
  if (!refreshToken) return false;
  const res = await doFetch("/token/refresh", {
    method: "POST",
    body: { refreshToken },
    auth: false,
  });
  if (!res.ok) {
    tokens.clear();
    return false;
  }
  const data = await res.json();
  tokens.set({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  });
  return true;
}

// Single-flight refresh: concurrent 401s share ONE /token/refresh call.
// (The refresh token rotates server-side, so parallel refreshes would revoke
// each other and log the user out.)
let refreshing = null;
function tryRefresh() {
  if (!refreshing) {
    refreshing = doRefresh().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

async function request(path, opts = {}) {
  let res = await doFetch(path, opts);

  // Transparently refresh once on 401, then retry.
  if (res.status === 401 && opts.auth !== false) {
    if (await tryRefresh()) {
      res = await doFetch(path, opts);
    }
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(
      data?.error || `request failed (${res.status})`,
      res.status,
    );
  }
  return data;
}

// --- Auth ---
export const authApi = {
  loginURL: (provider) => `${BACKEND_URL}/api/v1/auth/${provider}/login`,
  me: (signal) => request("/me", { signal }),
  guestLogin: () => request("/auth/guest", { method: "POST", auth: false }),
  logout: () =>
    request("/token/logout", {
      method: "POST",
      body: { refreshToken: tokens.refresh },
      auth: false,
    }),
};

// --- Workspaces ---
export const workspacesApi = {
  list: (signal) => request("/workspaces", { signal }),
  getById: (id, signal) => request(`/workspaces/${id}`, { signal }),
  create: ({ name, description = "" }) =>
    request("/workspaces", { method: "POST", body: { name, description } }),
  getMyRole: (workspaceId, signal) =>
    request(`/workspaces/${workspaceId}/me`, { signal }),
};

// --- Codices ---
export const codicesApi = {
  listByWorkspace: (workspaceId, signal) =>
    request(`/workspaces/${workspaceId}/codices`, { signal }),
  getById: (codexId, signal) => request(`/codices/${codexId}`, { signal }),
  create: ({ workspaceId, title, description = "", type, subtype }) =>
    request(`/workspaces/${workspaceId}/codices`, {
      method: "POST",
      body: {
        title,
        description,
        ...(type ? { type } : {}),
        ...(subtype ? { subtype } : {}),
      },
    }),
  patch: (codexId, { title }) =>
    request(`/codices/${codexId}`, {
      method: "PATCH",
      body: { title },
    }),
};

// --- Chapters ---
export const chaptersApi = {
  listByCodex: (codexId, signal) =>
    request(`/codices/${codexId}/chapters`, { signal }),
  getById: (chapterId, signal) => request(`/chapters/${chapterId}`, { signal }),
  getContent: (chapterId, signal) =>
    request(`/chapters/${chapterId}/content`, { signal }),
  create: ({ codexId, title, parentId = null, description = "" }) =>
    request(`/codices/${codexId}/chapters`, {
      method: "POST",
      body: { title, parentId, description },
    }),
  updateContent: (chapterId, content) =>
    request(`/chapters/${chapterId}/content`, {
      method: "PUT",
      body: { content },
    }),
  patch: (chapterId, { title, description, hidden }) => {
    const body = {};
    if (title !== undefined) body.title = title;
    if (description !== undefined) body.description = description;
    if (hidden !== undefined) body.hidden = hidden;
    return request(`/chapters/${chapterId}`, { method: "PATCH", body });
  },
  delete: (chapterId) =>
    request(`/chapters/${chapterId}`, { method: "DELETE" }),
  reorder: (codexId, orderedIds) =>
    request(`/codices/${codexId}/chapters/reorder`, {
      method: "POST",
      body: { orderedIds },
    }),
};

// --- Members (RBAC) ---
export const membersApi = {
  list: (workspaceId, signal) =>
    request(`/workspaces/${workspaceId}/members`, { signal }),
  add: (workspaceId, email, role) =>
    request(`/workspaces/${workspaceId}/members`, {
      method: "POST",
      body: { email, role },
    }),
  updateRole: (workspaceId, userId, role) =>
    request(`/workspaces/${workspaceId}/members/${userId}`, {
      method: "PATCH",
      body: { role },
    }),
  remove: (workspaceId, userId) =>
    request(`/workspaces/${workspaceId}/members/${userId}`, {
      method: "DELETE",
    }),
};
